import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MapPin, Car, Users } from 'lucide-react';
import { pricingData } from '../mockData';

const PricingSection = ({ title, description, destinations, gradient, vehicleType }) => {
  return (
    <Card className="overflow-hidden border-2 hover:shadow-2xl transition-all duration-300">
      <CardHeader className={`bg-gradient-to-br ${gradient} text-white p-4 sm:p-6`}>
        <CardTitle className="text-lg sm:text-2xl font-bold">{title}</CardTitle>
        <p className="text-white/90 text-xs sm:text-sm mt-1 sm:mt-2">{description}</p>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {destinations.map((item, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-2 sm:p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg sm:rounded-xl hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1">
                <MapPin className="text-blue-600 flex-shrink-0 hidden sm:block" size={16} />
                <span className="font-medium text-gray-900 text-xs sm:text-sm truncate">{item.destination}</span>
              </div>
              <span className="text-sm sm:text-lg font-bold text-blue-600 ml-1 sm:ml-2 flex-shrink-0">
                {vehicleType === 'small' ? item.small : item.big}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const Pricing = () => {
  const [vehicleType, setVehicleType] = useState('small');

  return (
    <section id="pricing" className="py-10 sm:py-16 bg-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Transfer <span className="text-blue-600">Prices</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fixed transparent prices from Corfu Airport and Port to all major destinations. No hidden fees.
          </p>
          <div className="mt-6 inline-block bg-yellow-100 border-2 border-yellow-400 rounded-full px-6 py-2">
            <p className="text-yellow-800 font-semibold">All prices from Corfu Airport and Port • Meet & Greet Included</p>
          </div>
        </div>

        {/* Vehicle Type Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-full p-2 shadow-lg">
            <button
              onClick={() => setVehicleType('small')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                vehicleType === 'small'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg scale-105'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Car size={20} />
              <span>Small Car</span>
              <span className="flex items-center space-x-1 text-sm opacity-90">
                <Users size={14} />
                <span>1-4</span>
              </span>
            </button>
            <button
              onClick={() => setVehicleType('big')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                vehicleType === 'big'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg scale-105'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Car size={20} />
              <span>Big Car</span>
              <span className="flex items-center space-x-1 text-sm opacity-90">
                <Users size={14} />
                <span>5-8</span>
              </span>
            </button>
          </div>
        </div>

        {/* Selected Vehicle Indicator */}
        <div className="text-center mb-8">
          <p className="text-gray-600">
            Showing prices for <span className="font-bold text-blue-600">
              {vehicleType === 'small' ? 'Small Car (1-4 passengers)' : 'Big Car (5-8 passengers)'}
            </span>
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 gap-6 sm:gap-8 max-w-6xl mx-auto">
          <PricingSection 
            title="North Corfu Transfers"
            description="Transfers to northern destinations and resorts"
            destinations={pricingData.north}
            gradient="from-blue-600 to-blue-800"
            vehicleType={vehicleType}
          />
          
          <PricingSection 
            title="Central & West Coast"
            description="Transfers to central Corfu and west coast areas"
            destinations={pricingData.central}
            gradient="from-cyan-600 to-blue-600"
            vehicleType={vehicleType}
          />
          
          <PricingSection 
            title="South Corfu Transfers"
            description="Transfers to southern destinations and beaches"
            destinations={pricingData.south}
            gradient="from-blue-700 to-cyan-700"
            vehicleType={vehicleType}
          />
        </div>

        {/* Additional Info */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl">
            <div className="text-3xl mb-2">💼</div>
            <h4 className="font-bold text-gray-900 mb-2">Business Rates</h4>
            <p className="text-sm text-gray-600">Special rates for corporate and regular clients</p>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl">
            <div className="text-3xl mb-2">👨‍👩‍👧‍👦</div>
            <h4 className="font-bold text-gray-900 mb-2">Child Seats</h4>
            <p className="text-sm text-gray-600">Free child seats available upon request</p>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl">
            <div className="text-3xl mb-2">🧳</div>
            <h4 className="font-bold text-gray-900 mb-2">Luggage Included</h4>
            <p className="text-sm text-gray-600">Standard luggage allowance included in price</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
